import React, { useEffect, useMemo } from "react";
import { RegisterOptions, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme, TextInput, Subheading, TouchableRipple } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { ACCOUNT } from "../../services/services-model.type";
import { formatNumberThousand } from "../../util/format-currency";
import { useDesktopMediaQuery } from "@media-query";
import { Block } from "@block";
import { InputText } from "../form/input";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import AccountSourceModal from "../modal/bene-account-source/accounts-source.modal";
import GlobalModal, { createGlobalModal, globalModal } from "../modal/global-modal/global-modal";
import { StyleSheet } from "react-native";
import { mapAccountPayments } from "app/shared/util/common";
import { getLimitRemainSelector, getListAccountSelector, getStatusListAccountSelector } from "app/modules/account-management/reducer/selectors";
import { Constant } from "app/shared/constant/constant";
import { fetchLimitRemainRequest, fetchListAccountRequest } from "app/modules/account-management/reducer/actions";
import { useNavigation } from "@react-navigation/native";

export interface SourceAccountProps {
  onChangeAccount: (accountDebit: ACCOUNT, isValid: boolean) => void;
  type?: Array<string>;
  account?: ACCOUNT;
  isSubmit?: boolean;
  limitRemain?: string;
  hiddenLimit?: boolean;
  isAccountDefault?: boolean;
}

enum FormEnum {
  accountNo,
  availableBalance,
  limitRemain,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export interface SourceAccountData {
  accountNo: string;
  availableBalance: string;
  limitRemain: string;
}

const SourceAccount = (props: SourceAccountProps) => {
  const { onChangeAccount = () => {}, type, account, isAccountDefault = false, isSubmit, limitRemain, hiddenLimit = false } = props;
  const { colors, fonts } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const { t } = useTranslation();
  const dispatchStore = useDispatch();
  const { addListener, removeListener } = useNavigation();

  const listAccountStore = useSelector(getListAccountSelector);
  const limitRemainStore = useSelector(getLimitRemainSelector);
  const statusGetListSore = useSelector(getStatusListAccountSelector);

  const [globalModalRef] = React.useState<React.RefObject<any>>(createGlobalModal());
  const [accountDebit, setAccountDebit] = React.useState<ACCOUNT>(account || {});
  const [listAccount, setListAccount] = React.useState<ACCOUNT[]>([]);
  const [refreshListAccount, setRefreshListAccount] = React.useState<boolean>(false);
  const [isFocus, setIsFocus] = React.useState<boolean>(false);

  const { control, setValue, getValues, trigger } = useForm<SourceAccountData>({
    defaultValues: {
      accountNo: "",
      availableBalance: "",
      limitRemain: "",
    },
    mode: "onChange",
  });

  const validateSchemeForm: ValidDataSchemeFormData = {
    accountNo: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN"),
      },
      validate: {
        status: (value) => {
          if (value?.length) {
            if (accountDebit.STATUS !== "A") {
              return t("TRANFER_THONG_BAO_TRANG_THAI_TAI_KHOAN_CHUYEN").toString();
            }
          }
          return true;
        },
        limitAmount: (value) => {
          if (value?.length) {
            if (accountDebit?.AVAILABLEBALANCE && Number(accountDebit.AVAILABLEBALANCE) <= 0) {
              return t("ACC_SO_DU_KHA_DUNG_NOT_ENOUGH").toString();
            }
          }
          return true;
        },
      },
    },
    availableBalance: {
      required: {
        value: false,
        message: "",
      },
    },
    limitRemain: {
      required: {
        value: false,
        message: "",
      },
    },
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        subheading: { fontWeight: "700" },
      }),
    [],
  );

  const getAccountDefault = React.useCallback(
    (list: ACCOUNT[]) => {
      if (!accountDebit?.ACCOUNTNO && list && list.length) {
        let accountDefault = list[0];
        const availAbleBalance = Number(accountDefault?.AVAILABLEBALANCE || 0);
        if ((accountDefault.STATUS !== "A" || availAbleBalance === 0) && list.length > 1) {
          const accountS = list.filter((item) => {
            const amount = Number(item?.AVAILABLEBALANCE || 0);
            if (type?.length && type.length === 1 && type[0] === Constant.ACCOUNTTYPE.ACCOUNT_PAYMENT) {
              return item.STATUS === "A" && amount > 0 && item.PRODUCTCODE === Constant.PRODUCT_CODE_PAYMENT;
            }
            return item.STATUS === "A" && amount > 0;
          });
          if (accountS && accountS.length) accountDefault = accountS[0];
        }
        return accountDefault;
      }
      return accountDebit;
    },
    [accountDebit, type],
  );

  const getListAccount = React.useCallback(
    (data?: ACCOUNT[]) => {
      let list: ACCOUNT[] = [];
      if (data?.length) {
        const filter: ACCOUNT[] = data.filter((item) => {
          if (type?.length) {
            return item?.ACCOUNTTYPE && type.includes(item.ACCOUNTTYPE) && item.CURRENCYCODE === "VND";
          } else {
            return item?.ACCOUNTTYPE && item.CURRENCYCODE === "VND";
          }
        });
        list = filter;
      }
      return mapAccountPayments(list);
    },
    [type],
  );

  const handlerSelectAccount = (acc: ACCOUNT) => {
    globalModal.hide(globalModalRef);
    setAccountDebit(acc);
  };

  const onPressChoose = () => {
    const content: JSX.Element = (
      <AccountSourceModal account={accountDebit} typeArray={type} checkCurrentCode={true} onPressAcceptAccount={handlerSelectAccount} />
    );
    globalModal.show(content, globalModalRef);
  };

  const onChange = React.useCallback(
    async (accountNew: ACCOUNT) => {
      if (accountNew?.ACCOUNTNO?.length && accountNew.ACCOUNTNO !== getValues("accountNo")) {
        setValue("accountNo", accountNew.ACCOUNTNO);
        setValue("availableBalance", formatNumberThousand(accountNew?.AVAILABLEBALANCE));
        const accountTrigger = await trigger("accountNo");
        onChangeAccount(accountNew, accountTrigger);
      }
    },
    [getValues, setValue, trigger, onChangeAccount],
  );

  useEffect(() => {
    if (listAccountStore?.ACCOUNT.length && statusGetListSore === "ok") {
      // setListAccount([]);
      const list = getListAccount(listAccountStore?.ACCOUNT);
      setListAccount(list);
    }
  }, [getListAccount, listAccountStore?.ACCOUNT, statusGetListSore]);

  useEffect(() => {
    if (listAccount.length) {
      let accountNew = getAccountDefault(listAccount);
      if (accountDebit?.ACCOUNTNO?.length) {
        const filterACC = listAccount.filter(item => {
          return item.ACCOUNTNO === accountDebit?.ACCOUNTNO;
        });
        if (filterACC.length) {
          accountNew = filterACC[0];
        }
      }
      setAccountDebit(accountNew);
    }
  }, [getAccountDefault, listAccount, accountDebit]);

  useEffect(() => {
    onChange(accountDebit);
  }, [onChange, accountDebit]);

  useEffect(() => {
    if (isAccountDefault && account?.ACCOUNTNO?.length && accountDebit?.ACCOUNTNO) {
      setAccountDebit(account);
    }
  }, [account, accountDebit, isAccountDefault]);

  useEffect(() => {
    if (!hiddenLimit) {
      let limit = limitRemain || limitRemainStore || "0";
      setValue("limitRemain", formatNumberThousand(limit));
    }
  }, [hiddenLimit, limitRemain, limitRemainStore, setValue]);

  useEffect(() => {
    if (isSubmit) {
      trigger("accountNo");
    }
  }, [isSubmit, trigger]);

  useEffect(() => {
    const subscribe = addListener('focus', () => {
      console.log('Vào Focus');
      // do something
      setIsFocus(true);
    });
    return subscribe;
  }, [addListener]);

  useEffect(() => {
    const unsubscribe = addListener('blur', () => {
      console.log('Vào blur');
      // do something
      setIsFocus(false);
    });
    return unsubscribe;
  }, [addListener]);

  useEffect(() => {
    console.log("================ isFocus: ", isFocus);
    if (isFocus) {
      dispatchStore(fetchListAccountRequest());
      dispatchStore(fetchLimitRemainRequest());
    }
  }, [isFocus]);

  const chooseAccount = <TextInput.Icon name="chevron-down" onPress={onPressChoose} />;
  const rightAmount = <TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />;

  return (
    <Block pt={16} row={isDesktop ? true : false}>
      <Block w={200}>
        <StackSpacing size="medium" />
        <Subheading style={styles.subheading} numberOfLines={1}>
          {t("TRANFER_TAI_KHOAN_CHUYEN")}
        </Subheading>
        <StackSpacing size="medium" />
      </Block>
      <Block flex={1} bg={colors.backgroundForm} br={12} p={16}>
        <TouchableRipple onPress={onPressChoose}>
          <InputText
            rules={validateSchemeForm.accountNo}
            name="accountNo"
            control={control}
            inputProps={{ right: chooseAccount, editable: false }}
            label={t("TRANFER_TAI_KHOAN_NGUON")}
          />
        </TouchableRipple>
        <Block spaced row={isDesktop ? true : false}>
          <Block flex={1}>
            <InputText
              rules={validateSchemeForm.availableBalance}
              name="availableBalance"
              control={control}
              inputProps={{ right: rightAmount, editable: false }}
              label={t("ACC_SO_DU_KHA_DUNG")}
            />
          </Block>
          <QueueSpacing size={hiddenLimit ? "none" : "huge"} />
          <Block flex={1} display={!hiddenLimit ? "flex" : "none"}>
            <InputText
              rules={validateSchemeForm.limitRemain}
              name="limitRemain"
              control={control}
              inputProps={{ right: rightAmount, editable: false }}
              label={t("ACC_HAN_MUC_GD")}
            />
          </Block>
        </Block>
      </Block>
      <GlobalModal ref={globalModalRef} />
    </Block>
  );
};

export default SourceAccount;
